'use client';

import React, { useState } from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/sections/Footer';
import BackgroundDecor from '@/components/BackgroundDecor';
import { Mail, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { brand } from '@/config/brand';
import { Seo } from '@/components/Seo';
import ExportedImage from "next-image-export-optimizer";
import { useTranslations } from '@/lib/i18n-client';
import { getWhatsAppHref } from '@/lib/contact';
import { getBrandClientPage } from '@/components/brands/brand-pages-client';

export default function ContactUsPage() {
  const BrandSupport = getBrandClientPage('support');
  const { t } = useTranslations();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  if (BrandSupport) {
    return <BrandSupport />;
  }

  const whatsappMessage = t("contactUs.whatsappMessage", "Hi! I have questions about your eSIM plans. Can you help me?");
  const whatsappLink = getWhatsAppHref(whatsappMessage);

  const openTawkChat = () => {
    try {
      const w = window as any;
      const api = w.Tawk_API || w.$tawk;
      if (api && typeof api.maximize === 'function') { api.maximize(); return; }
      w.Tawk_API = api || {};
      try { w.Tawk_API.onLoad = function () { try { w.Tawk_API.maximize?.(); } catch {} }; } catch {}
      let attempts = 0; const iv = setInterval(() => { const a = w.Tawk_API || w.$tawk; if (a && typeof a.maximize === 'function') { try { a.maximize(); } catch {} clearInterval(iv); } if (++attempts > 20) clearInterval(iv); }, 250);
    } catch {}
  };

  return (
    <div className="min-h-screen bg-background pattern-overlay relative overflow-hidden">
      <BackgroundDecor />
      <Seo route="/contact-us/" breadcrumbs={[{ name: t("breadcrumbs.home", "Home"), url: "/" }, { name: t("breadcrumbs.contactUs", "Contact Us"), url: "/contact-us/" }]} />
      <Header />
      <main className="pt-20">
        <section id="contact" className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">{t("contactUs.title", "Contact")} <span className="bg-gradient-primary bg-clip-text text-transparent">{t("contactUs.titleHighlight", "Us")}</span></h1>
              <p className="text-xl text-muted-foreground">{t("contactUs.subtitle", "Get in touch with our 24/7 support team")}</p>
              <div className="mx-auto mt-8 max-w-sm">
                <ExportedImage
                  src="/images/supra-support.jpg"
                  alt={t("contactUs.heroAlt", "Support agent ready to help with your eSIM")}
                  width={800}
                  height={600}
                  className="w-full h-auto rounded-3xl border border-border/50 shadow-xl"
                  sizes="(max-width: 640px) 100vw, 400px"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="glass rounded-xl p-8">
                <div className="flex items-center gap-3 mb-6"><Mail className="w-6 h-6 text-primary" /><h2 className="text-2xl font-bold">{t("contactUs.email.title", "Email Us")}</h2></div>
                {!submitted ? (
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault(); setEmailError(null);
                      const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
                      if (!validEmail) { setEmailError(t("contactUs.email.validation", "Please enter a valid email address.")); return; }
                      if (!name.trim() || !message.trim()) return; if (submitting) return; setSubmitting(true);
                      try {
                        const submittedAt = new Date().toISOString();
                        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                        const pageUrl = typeof window !== 'undefined' ? window.location.href : undefined;
                        const referrer = typeof document !== 'undefined' ? document.referrer : undefined;
                        const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
                        await fetch("/api/notify", {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            type: "contact",
                            name,
                            email,
                            message,
                            submittedAt,
                            timezone,
                            pageUrl,
                            referrer,
                            userAgent,
                          }),
                        });
                        setSubmitted(true);
                      } finally { setSubmitting(false); }
                    }}
                    className="space-y-4"
                  >
                    <Input id="contact-name" aria-label={t("contactUs.form.name", "Your Name")} placeholder={t("contactUs.form.name", "Your Name")} required value={name} onChange={(e) => setName(e.target.value)} />
                    <div>
                      <Input id="contact-email" aria-label={t("contactUs.form.email", "Email Address")} type="email" placeholder={t("contactUs.form.email", "Email Address")} required value={email} onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(null); }} />
                      {emailError && <div className="text-red-500 text-xs mt-1 pl-1">{emailError}</div>}
                    </div>
                    <Textarea id="contact-message" aria-label={t("contactUs.form.message", "How can we help you?")} placeholder={t("contactUs.form.message", "How can we help you?")} rows={5} required value={message} onChange={(e) => setMessage(e.target.value)} />
                    <Button type="submit" variant="hero" size="lg" className="w-full" disabled={submitting}>{submitting ? t("contactUs.form.sending", "Sending…") : t("contactUs.form.submit", "Send Message")}</Button>
                  </form>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-success mb-4 text-xl">{t("contactUs.success.title", "✓ Message sent successfully!")}</div>
                    <p className="text-muted-foreground">{t("contactUs.success.body", "We'll get back to you within 24 hours at {email}.").replace("{email}", brand.contact.email)}</p>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div className="glass rounded-xl p-8 hover:shadow-glow transition-smooth cursor-pointer" onClick={openTawkChat}>
                  <div className="flex items-center gap-4 mb-4"><div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center"><MessageCircle className="w-6 h-6 text-primary" /></div><div><h3 className="text-xl font-bold">{t("contactUs.liveChat.title", "Live Chat")}</h3><p className="text-sm text-success">{t("contactUs.liveChat.status", "● Online Now")}</p></div></div>
                  <p className="text-muted-foreground mb-4">{t("contactUs.liveChat.body", "Get instant answers from our support team")}</p>
                  <Button variant="outline" size="lg" className="w-full" onClick={openTawkChat}>{t("contactUs.liveChat.cta", "Start Live Chat")}</Button>
                </div>
                {whatsappLink && (
                <div className="glass rounded-xl p-8 hover:shadow-glow transition-smooth border-2 border-green-500/30">
                  <div className="flex items-center gap-4 mb-4"><div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center"><span className="text-2xl">💬</span></div><div><h3 className="text-xl font-bold">{t("contactUs.whatsapp.title", "WhatsApp")}</h3><p className="text-sm text-green-400">{t("contactUs.whatsapp.badge", "Fast Response")}</p></div></div>
                  <p className="text-muted-foreground mb-4">{t("contactUs.whatsapp.body", "Chat with us directly on WhatsApp for immediate assistance")}</p>
                  <Button variant="default" size="lg" className="w-full bg-green-600 hover:bg-green-700" asChild>
                    <a href={whatsappLink} target="_blank" rel="noopener noreferrer">{t("contactUs.whatsapp.cta", "Chat on WhatsApp")}</a>
                  </Button>
                </div>
                )}
                <div className="glass rounded-xl p-8 hover:shadow-glow transition-smooth border-2 border-blue-500/30">
                  <div className="flex items-center gap-4 mb-4"><div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center"><Mail className="w-6 h-6 text-blue-400" /></div><div><h3 className="text-xl font-bold">{t("contactUs.emailCard.title", "Email Us")}</h3><p className="text-sm text-blue-400">{t("contactUs.emailCard.badge", "2 Hour Response")}</p></div></div>
                  <p className="text-muted-foreground mb-4">{t("contactUs.emailCard.body", "Send us an email and we'll get back to you soon")}</p>
                  <div className="bg-primary/10 rounded-lg p-4 text-center">
                    <a href={`mailto:${brand.contact.email}`} className="text-lg font-semibold text-primary hover:text-primary/80 transition-colors">{brand.contact.email}</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
